/**
 * "Add a section" — the catalog-grid modal (module 22 editor parity).
 *
 * Replaces the plain dropdown with the wireframe's picker: each section type is
 * a card with an icon, name and one-line description. Singletons already on the
 * page are filtered out by the caller (`addable`), so nothing here is offered
 * and then rejected.
 */
import type { LucideIcon } from "lucide-react";
import {
  Megaphone,
  Image as ImageIcon,
  BarChart3,
  GalleryHorizontal,
  LayoutGrid,
  Grid3x3,
  Tag,
  BookOpen,
  PenLine,
  Columns2,
  MessageSquareQuote,
  HelpCircle,
  Award,
  Images,
  Star,
  MapPin,
  Mail,
  UserPlus,
} from "lucide-react";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@flint/ui";
import { SECTION_LABELS, type HomeSectionType } from "@flint/api-contract/store-home";

/** Icon + blurb per section type, shown on the picker card. */
const CATALOG: Record<HomeSectionType, { Icon: LucideIcon; description: string }> = {
  announcement_bar: { Icon: Megaphone, description: "Slim strip for promos, offers or notices" },
  hero_slider: { Icon: ImageIcon, description: "Big banner with heading and call-to-action" },
  stats_strip: { Icon: BarChart3, description: "Established, team, capacity, ships-from" },
  collection_rail: { Icon: GalleryHorizontal, description: "Horizontal line-sheet of a collection" },
  product_grid: { Icon: LayoutGrid, description: "Grid of pack-priced products" },
  category_grid: { Icon: Grid3x3, description: "Shop-by-collection image tiles" },
  promo_banner: { Icon: Tag, description: "Full-width accent CTA band" },
  about_block: { Icon: BookOpen, description: "Your store's about text" },
  rich_text: { Icon: PenLine, description: "Freeform heading and paragraph" },
  image_text: { Icon: Columns2, description: "Side-by-side image and copy block" },
  testimonials: { Icon: MessageSquareQuote, description: "Dealer reviews and quotes" },
  faq: { Icon: HelpCircle, description: "Common wholesale questions" },
  logo_wall: { Icon: Award, description: "Row of partner or brand logos" },
  gallery: { Icon: Images, description: "Responsive grid of images" },
  featured_product: { Icon: Star, description: "Spotlight one product with a CTA" },
  contact: { Icon: MapPin, description: "Contact details, address and WhatsApp" },
  newsletter: { Icon: Mail, description: "Email opt-in — reaches you on WhatsApp" },
  lead_capture: { Icon: UserPlus, description: "Dealer signup form — enquiries on WhatsApp" },
};

export function AddSectionModal({
  open,
  onOpenChange,
  addable,
  atCap,
  onPick,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  addable: HomeSectionType[];
  atCap: boolean;
  onPick: (type: HomeSectionType) => void;
}) {
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-w-2xl" style={{ maxWidth: "42rem" }}>
        <DialogHeader>
          <DialogTitle>Add a section</DialogTitle>
          <DialogDescription>
            Building blocks for your storefront — click one to add it to the page.
          </DialogDescription>
        </DialogHeader>

        {atCap ? (
          <p className="py-2 text-sm text-muted">
            You've reached the maximum number of sections. Remove one to add another.
          </p>
        ) : (
          <div className="mt-2 grid max-h-[58vh] grid-cols-1 gap-2.5 overflow-y-auto pr-1 sm:grid-cols-2">
            {addable.map((type) => {
              const { Icon, description } = CATALOG[type];
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => onPick(type)}
                  className="flex items-start gap-3 rounded-lg border border-line p-3 text-left transition hover:border-sky-600 hover:shadow-sm"
                >
                  <span className="grid size-9 shrink-0 place-items-center rounded-md bg-sky-50 text-sky-700">
                    <Icon size={18} aria-hidden />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-ink">
                      {SECTION_LABELS[type]}
                    </span>
                    <span className="block text-xs text-muted">{description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </DialogRoot>
  );
}
